import type { ClassValue } from "clsx";
import { clsx } from "clsx";
import { differenceInDays, format, formatRelative } from "date-fns";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// oxlint-disable-next-line typescript/no-explicit-any
export type AnyType = any;

export function isWithinLastWeek(date: Date) {
  const diffInDays = differenceInDays(date, new Date());
  // Only match past dates within the last 7 days (not future dates)
  return diffInDays <= 0 && diffInDays > -7;
}

export function detailedRelativeTime(date: Date) {
  // if was less then a week ago, show relative date
  // else show full date
  const isRecent = isWithinLastWeek(date);

  return {
    isRecent,
    date: isRecent
      ? formatRelative(date, new Date(), {}).replace(/^\w/, (c) =>
          c.toUpperCase(),
        ) // capitalize first letter
      : format(date, "MMM d, yyyy hh:mm a"),
  };
}

export async function waitMs(ms: number) {
  return await new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// oxfmt-ignore
// oxlint-disable-next-line typescript/no-explicit-any
export const deepEqual = function (x: Record<string, any>, y: Record<string, any>) {
  if (x === y) {
    return true;
  } else if (
    typeof x == "object" &&
    x != null &&
    typeof y == "object" &&
    y != null
  ) {
    if (Object.keys(x).length != Object.keys(y).length) return false;

    for (const prop in x) {
      if (Object.prototype.hasOwnProperty.call(y, prop)) {
        // oxlint-disable-next-line typescript/no-unsafe-argument
        if (!deepEqual(x[prop], y[prop])) return false;
      } else return false;
    }

    return true;
  } else return false;
};

/**
 * Formats a monetary amount (in cents) to a localized USD string using accounting notation
 * @param amount - The amount in cents. Can be number, string, null, or undefined
 * @param precision - Number of decimal places to show (default: 2)
 * @returns Formatted currency string (e.g. "$10.00", "($10.00)" for negatives) or "-" if amount is null/undefined
 */
export const formatCurrency = (
  amount: number | string | null | undefined,
  precision = 2,
) => {
  if (amount === null || amount === undefined) {
    return "-";
  }

  const amountInCents = typeof amount === "string" ? parseInt(amount) : amount;
  const dollars = amountInCents / 100;
  const isNegative = dollars < 0;

  // Remove the minus sign by using Math.abs before formatting
  const formatted = Math.abs(dollars).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    signDisplay: "never", // Ensures no sign is displayed
  });

  return isNegative ? `(${formatted})` : formatted;
};

/**
 * Alias for formatCurrency - kept for backward compatibility
 * @deprecated Use formatCurrency instead
 */
export const getDollar = formatCurrency;

export const hashString = (str: string) => {
  let hash = 0,
    i,
    chr;
  if (str.length === 0) return hash;
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

/**
 * Fallback UUID v4 generator for local development, randomUUID only works in https
 */
function uuidv4Fallback(): `${string}-${string}-${string}-${string}-${string}` {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (crypto.getRandomValues(new Uint8Array(1))[0]! & (15 >> (Number(c) / 4)))
    ).toString(16),
  ) as `${string}-${string}-${string}-${string}-${string}`;
}

export function uuidv4() {
  return typeof crypto.randomUUID === "function"
    ? (crypto.randomUUID() as `${string}-${string}-${string}-${string}-${string}`)
    : uuidv4Fallback();
}

/**
 * Formats a number of megabytes to a more readable format
 * @param mb - The number of megabytes to format
 * @returns Formatted string with MB or GB unit
 * @example formatMB(500) // "500 MB"
 * @example formatMB(1024) // "1.0 GB"
 * @example formatMB(1536) // "1.5 GB"
 */
/**
 * Formats a value in cents as USD currency
 * @param cents - The amount in cents
 * @returns Formatted USD string (e.g., "$12.50")
 */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

export const formatMB = (mb: number) => {
  if (mb >= 1024) {
    return (
      Intl.NumberFormat("en-US", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(mb / 1024) + " GB"
    );
  }
  return Math.round(mb) + " MB";
};
